import { PlanItemModel } from '@/libs/Api';
import { graphqlPost } from '@/libs/tpm-analytics/graphql/transport';
import { buildPlanItemsQuery, buildPlanItemsWithResponsesQuery, buildPanelDashboardPlanItemsQuery, toIsoString } from '@/libs/tpm-analytics/graphql/queries';
import { buildPanelDashboardPlanItemsWhere } from '@/libs/tpm-analytics/graphql/predicates';

/**
 * Repository for reading unplanned shutdown plan items (maintenance failures) from GraphQL.
 */
export class PlanItemsRepository {
    static async fetchShutdownPlanItemsForRange(
        _predicates: any[],
        fromIso?: string,
        toIso?: string,
        filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[]; faultTypeIds?: string[] }
    ): Promise<PlanItemModel[]> {
        const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
        const parts: string[] = [
            `isWorkplaceShutdown: { eq: true }`,
            `auditFaultType: { isPlannedShutdown: { eq: false } }`,
            // Používáme striktní nerovnosti kvůli výrobnímu dni (konec výrobního dne je začátek následujícího)
            `shutdownFromDate: { gt: $from }`,
            `shutdownToDate: { lt: $to }`,
        ];
        const f = filters || {};
        if (f.workplaceIds && f.workplaceIds.length > 0) parts.push(`workplaceId: { in: ${listExpr(f.workplaceIds)} }`);
        if (f.locationIds && f.locationIds.length > 0) parts.push(`workplace: { locationId: { in: ${listExpr(f.locationIds)} } }`);
        if (f.faultTypeIds && f.faultTypeIds.length > 0) parts.push(`auditFaultTypeId: { in: ${listExpr(f.faultTypeIds)} }`);
        if (f.projectIds && f.projectIds.length > 0) parts.push(`shift: { shiftProductions: { some: { projectId: { in: ${listExpr(f.projectIds)} } } } }`);
        if (f.shiftTypeIds && f.shiftTypeIds.length > 0) parts.push(`shift: { shiftTypeId: { in: ${listExpr(f.shiftTypeIds)} } }`);
        if (f.shiftTeamTypeIds && f.shiftTeamTypeIds.length > 0) parts.push(`shift: { shiftTeamTypeId: { in: ${listExpr(f.shiftTeamTypeIds)} } }`);
        const where = `{ ${parts.join(', ')} }`;
        const query = buildPlanItemsQuery(where);
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][PlanItems] fetchShutdownPlanItemsForRange', { from: fromIso, to: toIso, filters });
        }
        const data = await graphqlPost<any>(query, { from: toIsoString(fromIso), to: toIsoString(toIso) });
        const list = data?.planItems ?? [];
        return Array.isArray(list) ? (list as PlanItemModel[]) : [];
    }

    /**
     * Fetch planned shutdown plan items (planned downtime) that represent actual workplace shutdowns.
     */
    static async fetchPlannedShutdownPlanItemsForRange(
        _predicates: any[],
        fromIso?: string,
        toIso?: string,
        filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
    ): Promise<PlanItemModel[]> {
        const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
        const parts: string[] = [
            `isWorkplaceShutdown: { eq: true }`,
            `auditFaultType: { isPlannedShutdown: { eq: true } }`,
            // Používáme striktní nerovnosti kvůli výrobnímu dni (konec výrobního dne je začátek následujícího)
            `shutdownFromDate: { gt: $from }`,
            `shutdownToDate: { lt: $to }`,
        ];
        const f = filters || {};
        if (f.workplaceIds && f.workplaceIds.length > 0) parts.push(`workplaceId: { in: ${listExpr(f.workplaceIds)} }`);
        if (f.locationIds && f.locationIds.length > 0) parts.push(`workplace: { locationId: { in: ${listExpr(f.locationIds)} } }`);
        if (f.projectIds && f.projectIds.length > 0) parts.push(`shift: { shiftProductions: { some: { projectId: { in: ${listExpr(f.projectIds)} } } } }`);
        if (f.shiftTypeIds && f.shiftTypeIds.length > 0) parts.push(`shift: { shiftTypeId: { in: ${listExpr(f.shiftTypeIds)} } }`);
        if (f.shiftTeamTypeIds && f.shiftTeamTypeIds.length > 0) parts.push(`shift: { shiftTeamTypeId: { in: ${listExpr(f.shiftTeamTypeIds)} } }`);
        const where = `{ ${parts.join(', ')} }`;
        const query = buildPlanItemsQuery(where);
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][PlanItems] fetchPlannedShutdownPlanItemsForRange', { from: fromIso, to: toIso, filters });
        }
        const data = await graphqlPost<any>(query, { from: toIsoString(fromIso), to: toIsoString(toIso) });
        const list = data?.planItems ?? [];
        return Array.isArray(list) ? (list as PlanItemModel[]) : [];
    }

    /**
     * Fetch unplanned shutdown plan items with responses (for longest shutdowns table).
     */
    static async fetchShutdownPlanItemsWithResponsesForRange(
        _predicates: any[],
        fromIso?: string,
        toIso?: string,
        filters?: { locationIds?: string[]; workplaceIds?: string[]; projectIds?: string[]; shiftTypeIds?: string[]; shiftTeamTypeIds?: string[] }
    ): Promise<PlanItemModel[]> {
        const listExpr = (ids?: string[]) => (ids && ids.length > 0 ? `[${ids.map((x) => `"${x}"`).join(', ')}]` : '[]');
        const parts: string[] = [
            `isWorkplaceShutdown: { eq: true }`,
            `auditFaultType: { isPlannedShutdown: { eq: false } }`,
            `shutdownFromDate: { gt: $from }`,
            `shutdownToDate: { lt: $to }`,
        ];
        const f = filters || {};
        if (f.workplaceIds && f.workplaceIds.length > 0) parts.push(`workplaceId: { in: ${listExpr(f.workplaceIds)} }`);
        if (f.locationIds && f.locationIds.length > 0) parts.push(`workplace: { locationId: { in: ${listExpr(f.locationIds)} } }`);
        if (f.projectIds && f.projectIds.length > 0) parts.push(`shift: { shiftProductions: { some: { projectId: { in: ${listExpr(f.projectIds)} } } } }`);
        if (f.shiftTypeIds && f.shiftTypeIds.length > 0) parts.push(`shift: { shiftTypeId: { in: ${listExpr(f.shiftTypeIds)} } }`);
        if (f.shiftTeamTypeIds && f.shiftTeamTypeIds.length > 0) parts.push(`shift: { shiftTeamTypeId: { in: ${listExpr(f.shiftTeamTypeIds)} } }`);
        const where = `{ ${parts.join(', ')} }`;
        const query = buildPlanItemsWithResponsesQuery(where);
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][PlanItems] fetchShutdownPlanItemsWithResponsesForRange', { from: fromIso, to: toIso, filters });
        }
        const data = await graphqlPost<any>(query, { from: toIsoString(fromIso), to: toIsoString(toIso) });
        const list = data?.planItems ?? [];
        return Array.isArray(list) ? (list as PlanItemModel[]) : [];
    }

    /**
     * Fetch plan items for Panel Dashboard
     * Filters: processType = IssueSolving AND time <= referenceDateTime
     * AND (planItemState = WaitingToPerform OR (isWorkplaceShutdown AND shutdown conditions))
     */
    static async fetchPanelDashboardPlanItems(
        referenceDateTime: string,
        filters?: { workplaceIds?: string[]; locationIds?: string[] }
    ): Promise<PlanItemModel[]> {
        const where = buildPanelDashboardPlanItemsWhere({
            workplaceIds: filters?.workplaceIds,
            locationIds: filters?.locationIds,
        });
        const query = buildPanelDashboardPlanItemsQuery(where);
        if ((window as any).__TPM_GQL_DEBUG) {
            // eslint-disable-next-line no-console
            console.log('[TPM][Repo][PlanItems] fetchPanelDashboardPlanItems', { referenceDateTime, filters });
        }
        const data = await graphqlPost<any>(query, { referenceDateTime: toIsoString(referenceDateTime) });
        const list = data?.planItems ?? [];
        return Array.isArray(list) ? (list as PlanItemModel[]) : [];
    }
}


